import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, Search, Sparkles, X, Sun, Moon, Droplet } from "lucide-react";
import { api } from "@/lib/api";
import { useTheme } from "@/contexts/ThemeContext";
import ThemeToggle from "@/components/ThemeToggle";
import AccentPicker from "@/components/AccentPicker";
import PushSubscribeButton from "@/components/PushSubscribeButton";
import PopuIaModal from "@/components/PopuIaModal";

function PopuLogo({ size = "text-3xl" }) {
  return (
    <div className="text-center select-none">
      <Link to="/" data-testid="header-logo-link" className="block">
        <span className={`popu-logo ${size}`} style={{ color: "var(--text)" }}>
          POPU<span className="dot-news accent-text">.news</span>
        </span>
      </Link>
      <a
        href="https://www.influcastbo.com"
        target="_blank"
        rel="noreferrer"
        data-testid="header-influcast-link"
        className="text-[8px] tracking-[0.22em] font-bold uppercase mt-0.5 inline-block hover:opacity-80 transition"
        style={{ color: "var(--text-muted)" }}
      >
        by <span className="accent-text">✦ INFLUCAST</span>
      </a>
    </div>
  );
}

export default function Header() {
  const [cats, setCats] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [iaOpen, setIaOpen] = useState(false);
  const { theme, setTheme, accent, setAccent } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    api.get("/categories").then((r) => {
      // Respect `menu_visible` flag (legacy docs without the field default to visible)
      const list = (r.data || []).filter((c) => c.menu_visible !== false);
      setCats(list);
    }).catch(() => {});
  }, []);

  const activeSlug = (() => {
    const m = location.pathname.match(/^\/categoria\/([^/]+)/);
    if (m) return m[1];
    if (location.pathname === "/") return "actualidad";
    return null;
  })();

  const submitSearch = (e) => {
    e.preventDefault();
    if (searchQ.trim()) {
      navigate(`/buscar?q=${encodeURIComponent(searchQ.trim())}`);
      setSearchOpen(false);
      setSearchQ("");
    }
  };

  return (
    <header
      className="sticky top-0 z-50 border-b"
      style={{ background: "var(--bg)", borderColor: "var(--border)" }}
      data-testid="site-header"
    >
      <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-3 md:py-4 grid grid-cols-3 items-center">
        <div>
          <button
            data-testid="header-menu-button"
            onClick={() => setMenuOpen(true)}
            className="p-2 hover:opacity-70 transition -ml-2"
            aria-label="Abrir menú"
          >
            <Menu size={24} strokeWidth={2.5} />
          </button>
        </div>
        <PopuLogo size="text-xl md:text-3xl" />
        <div className="flex justify-end items-center gap-1 md:gap-2">
          <ThemeToggle testId="header-theme-toggle" size={18} />
          <AccentPicker testId="header-accent-picker" size={18} />
          <div className="hidden md:flex items-center gap-1 md:gap-2">
            <PushSubscribeButton />
            <button
              type="button"
              onClick={() => setIaOpen(true)}
              data-testid="header-popu-ia"
              aria-label="POPU IA"
              title="POPU IA"
              className="p-2 hover:opacity-70 transition relative"
              style={{ color: "var(--accent)" }}
            >
              <Sparkles size={20} strokeWidth={2.5} />
            </button>
            <button
              data-testid="header-search-button"
              onClick={() => setSearchOpen(true)}
              className="p-2 hover:opacity-70 transition -mr-2"
              aria-label="Buscar"
            >
              <Search size={20} strokeWidth={2.5} />
            </button>
          </div>
        </div>
      </div>

      {/* Category tabs - hidden on mobile (replaced by bottom nav) */}
      <nav
        className="hidden md:block border-t no-scrollbar overflow-x-auto"
        style={{ borderColor: "var(--border)" }}
        data-testid="category-tabs"
      >
        <ul className="max-w-[1440px] mx-auto px-2 md:px-8 flex items-center md:justify-center gap-1 md:gap-8 whitespace-nowrap">
          {cats.map((c) => {
            const isActive = activeSlug === c.slug;
            return (
              <li key={c.slug}>
                <Link
                  to={`/categoria/${c.slug}`}
                  data-testid={`tab-${c.slug}`}
                  className="block px-2 md:px-0 py-3 md:py-4 text-[11px] md:text-sm font-bold uppercase tracking-[0.14em] md:tracking-[0.18em] transition relative"
                  style={{
                    color: isActive ? "var(--accent)" : "var(--text-muted)",
                  }}
                >
                  {c.menu_label || c.name}
                  {isActive && (
                    <span
                      className="absolute left-2 right-2 md:left-0 md:right-0 -bottom-[1px] h-[3px]"
                      style={{ background: "var(--accent)" }}
                    />
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Search overlay */}
      {searchOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-start justify-center pt-32 px-4"
          style={{ background: "rgba(0,0,0,0.7)" }}
          onClick={() => setSearchOpen(false)}
          data-testid="search-overlay"
        >
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={submitSearch}
            className="w-full max-w-2xl bg-white p-6 rounded-sm"
            style={{ background: "var(--bg)" }}
          >
            <div className="flex items-center gap-3 border-b pb-3" style={{ borderColor: "var(--border)" }}>
              <Search size={22} />
              <input
                autoFocus
                data-testid="search-input"
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                placeholder="Buscar en popu.news..."
                className="flex-1 bg-transparent outline-none text-lg"
              />
              <button type="button" onClick={() => setSearchOpen(false)}>
                <X size={20} />
              </button>
            </div>
            <p className="text-xs mt-3" style={{ color: "var(--text-muted)" }}>
              Enter para buscar
            </p>
          </form>
        </div>
      )}

      {/* Side menu */}
      {menuOpen && (
        <div
          className="fixed inset-0 z-[60]"
          style={{ background: "rgba(0,0,0,0.5)" }}
          onClick={() => setMenuOpen(false)}
          data-testid="side-menu-overlay"
        >
          <aside
            onClick={(e) => e.stopPropagation()}
            className="fixed top-0 left-0 h-full w-[320px] max-w-[85vw] p-8 overflow-y-auto"
            style={{ background: "var(--bg)" }}
          >
            <div className="flex items-center justify-between mb-8">
              <span className="popu-logo text-2xl">POPU<span className="dot-news accent-text">.news</span></span>
              <button onClick={() => setMenuOpen(false)} data-testid="side-menu-close">
                <X size={24} />
              </button>
            </div>
            <p className="overline mb-3" style={{ color: "var(--text-muted)" }}>Secciones</p>
            <ul className="flex flex-col gap-1 mb-8">
              {cats.map((c) => (
                <li key={c.slug}>
                  <Link
                    onClick={() => setMenuOpen(false)}
                    to={`/categoria/${c.slug}`}
                    className="block py-2 font-headline text-2xl uppercase hover:opacity-70"
                    style={{ color: "var(--text)" }}
                    data-testid={`menu-link-${c.slug}`}
                  >
                    {c.menu_label || c.name}
                  </Link>
                </li>
              ))}
            </ul>

            <p className="overline mb-3" style={{ color: "var(--text-muted)" }}>Tema</p>
            <div className="flex gap-2 mb-4">
              <button
                data-testid="theme-light-btn"
                onClick={() => setTheme("light")}
                className={`btn-ghost ${theme === "light" ? "accent-border accent-text" : ""}`}
              >
                <Sun size={14} className="inline mr-1" /> Claro
              </button>
              <button
                data-testid="theme-dark-btn"
                onClick={() => setTheme("dark")}
                className={`btn-ghost ${theme === "dark" ? "accent-border accent-text" : ""}`}
              >
                <Moon size={14} className="inline mr-1" /> Oscuro
              </button>
            </div>

            <p className="overline mb-3" style={{ color: "var(--text-muted)" }}>Acento</p>
            <div className="flex gap-2">
              <button
                data-testid="accent-pink-btn"
                onClick={() => setAccent("pink")}
                className="btn-ghost flex items-center gap-2"
                style={accent === "pink" ? { borderColor: "#FF1B6B", color: "#FF1B6B" } : {}}
              >
                <span className="inline-block w-3 h-3 rounded-full" style={{ background: "#FF1B6B" }} /> Pink
              </button>
              <button
                data-testid="accent-blue-btn"
                onClick={() => setAccent("blue")}
                className="btn-ghost flex items-center gap-2"
                style={accent === "blue" ? { borderColor: "#0040FF", color: "#0040FF" } : {}}
              >
                <span className="inline-block w-3 h-3 rounded-full" style={{ background: "#0040FF" }} /> Blue
              </button>
            </div>
          </aside>
        </div>
      )}
      <PopuIaModal open={iaOpen} onClose={() => setIaOpen(false)} />
    </header>
  );
}
